'use client'

/**
 * Icone dei **documenti della partita** (video e referto) per l'header del match
 * center pubblico.
 *
 * Regola: **tutti gli utenti afferenti allo stesso campionato** della gara
 * vedono i documenti, anche di gironi diversi; gli utenti di altri campionati
 * no. Il componente chiama lo stesso endpoint dell'area riservata
 * (`/api/admin/matches/<girone>/<numero>/assets`), che applica questo perimetro;
 * se la risposta non è 200 (ospite anonimo o altro campionato) non renderizza
 * nulla.
 *
 * Qui il documento si **apre** nel browser (nuova scheda: il proxy Nextcloud
 * serve il video `inline` e il PDF nel lettore). Il pannello dell'area riservata
 * — analisi del referto, navigazione dei set, offset di inizio del video — resta
 * uno strumento riservato: quelle letture hanno autorizzazioni proprie.
 */
import { useEffect, useState } from 'react'
import type { MatchAsset } from '@/lib/match-assets'

function IconVideo() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9A2.25 2.25 0 004.5 18.75z" />
    </svg>
  )
}

function IconDocument() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  )
}

export function MatchDocumentsLinks({
  poolSlug,
  matchNumber,
  stagioneId,
}: {
  poolSlug?: string | null
  matchNumber: string
  /** Stagione della gara: il match center può mostrare anche stagioni passate. */
  stagioneId?: string | null
}) {
  // `null` = non ancora risposto (niente placeholder: chi non è autorizzato non
  // deve vedere un'icona che poi sparisce).
  const [assets, setAssets] = useState<MatchAsset[] | null>(null)

  useEffect(() => {
    if (!poolSlug) {
      setAssets([])
      return
    }
    let cancelled = false
    const query = stagioneId ? `?stagione=${encodeURIComponent(stagioneId)}` : ''
    fetch(`/api/admin/matches/${poolSlug}/${matchNumber}/assets${query}`, { cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => { if (!cancelled) setAssets((data?.assets ?? []) as MatchAsset[]) })
      .catch(() => { if (!cancelled) setAssets([]) })
    return () => { cancelled = true }
  }, [poolSlug, matchNumber, stagioneId])

  const documents = (assets ?? []).filter((asset) => asset.type === 'video' || asset.type === 'document')
  if (documents.length === 0) return null

  return (
    <span className="flex shrink-0 items-center gap-0.5">
      {/* Il percorso è già l'URL utilizzabile: per Nextcloud è il proxy
          `/api/nxc/file?path=…`, per gli altri il percorso del file. */}
      {documents.map((asset) => (
        <a
          key={asset.id}
          href={asset.filePath}
          target="_blank"
          rel="noreferrer"
          title={asset.label || asset.filename}
          aria-label={`${asset.type === 'video' ? 'Video' : 'Referto'}: ${asset.label || asset.filename}`}
          className="flex h-7 w-7 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/10 hover:text-white"
        >
          {asset.type === 'video' ? <IconVideo /> : <IconDocument />}
        </a>
      ))}
    </span>
  )
}
